import { supabase } from '../../lib/supabase'

/**
 * The teams an administrator manages, read straight from muster.teams (any
 * signed-in account reads them, teams_read_all) with the supervisor's name
 * resolved from the accounts an administrator reads. Created only through
 * v1-create-team, which checks the caller is an active administrator and
 * audits the change.
 */
export type TeamRow = {
  id: string
  name: string
  fieldOffice: string
  supervisorId: string | null
  openDate: string
  closeDate: string | null
}

export type FieldOffice = { id: string; name: string }

type Row = {
  id: string
  name: string
  supervisor_id: string | null
  open_date: string
  close_date: string | null
  field_offices: { name: string } | null
}

export async function getTeams(): Promise<TeamRow[]> {
  const { data, error } = await supabase
    .from('teams')
    .select('id, name, supervisor_id, open_date, close_date, field_offices(name)')
    .order('name', { ascending: true })
  if (error || !data) return []
  return (data as unknown as Row[]).map((row) => ({
    id: row.id,
    name: row.name,
    fieldOffice: row.field_offices?.name ?? '',
    supervisorId: row.supervisor_id,
    openDate: row.open_date,
    closeDate: row.close_date,
  }))
}

export async function getFieldOffices(): Promise<FieldOffice[]> {
  const { data, error } = await supabase.from('field_offices').select('id, name').order('name', { ascending: true })
  if (error || !data) return []
  return data as FieldOffice[]
}

export type TeamStatus = 'open' | 'temporary' | 'closing' | 'closed' | 'upcoming'

/**
 * Where a team stands today: not open yet, open, open until a known date (a
 * temporary team), open through today only, or closed. A close date is the
 * last day a team is open, the same as a transfer treats it. Compared as ISO
 * dates, which order the same as the calendar.
 */
export function teamStatus(team: Pick<TeamRow, 'openDate' | 'closeDate'>, today: string): TeamStatus {
  if (team.openDate > today) return 'upcoming'
  if (team.closeDate !== null && team.closeDate < today) return 'closed'
  if (team.closeDate === null) return 'open'
  return team.closeDate === today ? 'closing' : 'temporary'
}

// "Sep 24, 2026" from "2026-09-24", read as the calendar date it names
// whatever the timezone.
export function dateLabel(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}
